import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, ChevronDown, WandSparkles, X, XIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Separator } from '../../ui/separator';

/** Default number of selected-item badges shown before collapsing into a "+N" overflow badge. Shared with `GroupedMultiSelect`. */
export const DEFAULT_MULTI_SELECT_MAX_COUNT = 3;

const multiSelectVariants = cva('mx-0.5 my-0.5 px-2 py-0 font-normal', {
  defaultVariants: {
    variant: 'default',
  },
  variants: {
    variant: {
      compact:
        'border-transparent bg-badge text-badge-foreground hover:bg-badge/80 h-5 px-1.5 text-xs font-normal shrink-0 gap-0.5 max-w-[7rem]',
      default: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
      secondary: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
    },
  },
});

export interface MultiSelectOption {
  /** Optional icon element to display next to the label */
  icon?: React.ComponentType<{ className?: string }>;
  /** Label displayed in the dropdown and badge */
  label: string;
  /** The value for this option */
  value: string;
}

/** A non-checkable, non-clickable heading with its own set of selectable options. */
export interface MultiSelectSection {
  /** Section heading text; omit for a pinned, heading-less section. */
  heading?: string;
  /** Selectable options within this section */
  options: MultiSelectOption[];
}

export interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof multiSelectVariants> {
  /** Whether to show the animation on the magic wand icon */
  animation?: number;
  /** Enables async/server-driven search: disables client-side filtering, hides "Select All", and streams keystrokes to `onSearchValueChange`. */
  async?: boolean;
  /** Text to display for the "Clear" action */
  clearText?: string;
  /** Default selected values on mount (uncontrolled) */
  defaultValue?: string[];
  /** Text shown when no options match the search */
  emptyText?: string;
  /**
   * Options shown as selected badges in the trigger but NOT offered in the dropdown list. Use to
   * keep a persisted, now-out-of-filter selection visible/removable (data drift) without making it
   * re-selectable. Merged into the trigger's label lookup only; the dropdown still uses `options`.
   */
  extraSelectedOptions?: MultiSelectOption[];
  /** Whether to hide the "Clear" button in the dropdown footer */
  hideClear?: boolean;
  /** Whether to hide the "Close" button in the dropdown footer */
  hideClose?: boolean;
  /** Whether to hide the Clear/Close footer */
  hideFooter?: boolean;
  /** Whether to hide the "Select All" toggle */
  hideSelectAll?: boolean;
  /** Shows a loading indicator in the dropdown list (async mode). */
  isLoading?: boolean;
  /** Visible label prefix (e.g., "Roles:", "Status:"). Always visible when nothing is selected; stays visible when items are selected only if `showLabelWhenFiltering` is true. */
  label?: string;
  /** Text shown next to the loading indicator while `isLoading` is true (async mode). */
  loadingText?: string;
  /**
   * Values that, while currently selected, cannot be removed through any interaction path (badge
   * remove, clear-all, footer Clear, unselect-all, backspace, overflow clear) or toggled off from
   * the dropdown. Does not auto-select a value that isn't already part of the selection.
   */
  lockedValues?: string[];
  /** Maximum number of items to display before showing "+N more" */
  maxCount?: number;
  /** Whether to use a modal popover on small screens */
  modalPopover?: boolean;
  /** Called whenever the dropdown opens or closes (click-outside, Escape, footer Close, trigger toggle). */
  onOpenChange?: (open: boolean) => void;
  /** Raw search text on every keystroke when `async` is true; caller owns debouncing and fetching. */
  onSearchValueChange?: (value: string) => void;
  /** Callback when the selected values change */
  onValueChange: (value: string[]) => void;
  /** The list of options to display. Ignored when `sections` is provided. */
  options?: MultiSelectOption[];
  /** Whether the overflow count indicator is clearable (shows as badge with remove icon) or just a plain label */
  overflowClearable?: boolean;
  /** Text displayed in the trigger when no options are selected (e.g., "All", "Select options"). */
  placeholder?: string;
  /** Text to display for the "Close" action in the dropdown footer */
  resetText?: string;
  /** Hint rendered after the last option group (e.g. a truncated-results message). Purely presentational — caller decides when it applies. */
  resultsHint?: string;
  /** Placeholder for the search input */
  searchPlaceholder?: string;
  /** Hint shown while `async` is true and the search text is empty. */
  searchPrompt?: string;
  /** Whether to show a search input in the dropdown */
  searchable?: boolean;
  /** Text to display for the "Select All" option */
  selectAllText?: string;
  /** Renders options grouped under non-selectable section headings instead of a flat list; ignores `options` and hides "Select All". */
  sections?: MultiSelectSection[];
  /** Whether to keep the label visible when items are selected */
  showLabelWhenFiltering?: boolean;
  /** Text to display for the "Unselect All" toggle when all items are selected */
  unselectAllText?: string;
  /**
   * Controlled selected values. When provided, the component is controlled and `defaultValue` is
   * ignored. The parent is responsible for updating this prop in response to `onValueChange`.
   */
  value?: string[];
}

// ─── State hook ───────────────────────────────────────────────────────────────

interface UseMultiSelectReturn {
  clearExtraOptions: (maxCount: number) => void;
  handleClear: () => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  isAnimating: boolean;
  isPopoverOpen: boolean;
  selectedValues: string[];
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleAll: () => void;
  toggleOption: (option: string) => void;
}

function useMultiSelect(
  value: string[] | undefined,
  defaultValue: string[] | undefined,
  onValueChange: (value: string[]) => void,
  options: MultiSelectOption[],
  lockedValues: string[],
): UseMultiSelectReturn {
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState<string[]>(defaultValue ?? []);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);

  // Sync internal state when defaultValue changes (uncontrolled only).
  const defaultValueKey = JSON.stringify(defaultValue ?? []);
  React.useEffect(() => {
    if (!isControlled) {
      setInternal(defaultValue ?? []);
    }
  }, [defaultValueKey]); // isControlled is derived from `value` which is not expected to toggle at runtime

  const selectedValues = isControlled ? value : internal;

  function commit(next: string[]): void {
    if (!isControlled) {
      setInternal(next);
    }
    onValueChange(next);
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      setIsPopoverOpen(true);
    } else if (event.key === 'Backspace' && !event.currentTarget.value) {
      const removable = [...selectedValues].reverse().find((v) => !lockedValues.includes(v));
      if (removable !== undefined) {
        commit(selectedValues.filter((v) => v !== removable));
      }
    }
  }

  function toggleOption(option: string): void {
    const isSelected = selectedValues.includes(option);
    if (isSelected && lockedValues.includes(option)) return;
    const next = isSelected ? selectedValues.filter((v) => v !== option) : [...selectedValues, option];
    commit(next);
  }

  function handleClear(): void {
    commit(selectedValues.filter((v) => lockedValues.includes(v)));
  }

  function clearExtraOptions(maxCount: number): void {
    commit(selectedValues.filter((v, index) => index < maxCount || lockedValues.includes(v)));
  }

  function toggleAll(): void {
    if (selectedValues.length === options.length) {
      commit(selectedValues.filter((v) => lockedValues.includes(v)));
    } else {
      commit(options.map((o) => o.value));
    }
  }

  return {
    clearExtraOptions,
    handleClear,
    handleInputKeyDown,
    isAnimating,
    isPopoverOpen,
    selectedValues,
    setIsAnimating,
    setIsPopoverOpen,
    toggleAll,
    toggleOption,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SelectedBadgeProps {
  animation: number;
  isAnimating: boolean;
  isLocked?: boolean;
  onRemove: () => void;
  option: MultiSelectOption;
  variant: MultiSelectProps['variant'];
}

function SelectedBadge({
  animation,
  isAnimating,
  isLocked = false,
  onRemove,
  option,
  variant,
}: SelectedBadgeProps): React.JSX.Element {
  const IconComponent = option.icon;
  return (
    <Badge
      className={cn(isAnimating ? 'animate-bounce' : '', multiSelectVariants({ variant }))}
      style={{ animationDuration: `${String(animation)}s` }}
    >
      {IconComponent && <IconComponent className="me-2 h-4 w-4" />}
      <span className="max-w-[120px] truncate">{option.label}</span>
      {!isLocked && (
        <span
          aria-label={`Remove ${option.label}`}
          className="ms-1 inline-flex cursor-pointer items-center"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <X aria-hidden="true" className="h-3 w-3 shrink-0 cursor-pointer hover:text-foreground" />
        </span>
      )}
    </Badge>
  );
}

interface OverflowBadgeProps {
  animation: number;
  clearable: boolean;
  count: number;
  isAnimating: boolean;
  onClearExtra: () => void;
  variant: MultiSelectProps['variant'];
}

function OverflowBadge({
  animation,
  clearable,
  count,
  isAnimating,
  onClearExtra,
  variant,
}: OverflowBadgeProps): React.JSX.Element {
  if (!clearable) {
    return <span className="ml-1.5 text-sm text-muted-foreground">{`+${String(count)}`}</span>;
  }

  return (
    <Badge
      className={cn(isAnimating ? 'animate-bounce' : '', multiSelectVariants({ variant }))}
      style={{ animationDuration: `${String(animation)}s` }}
    >
      {`+${String(count)}`}
      <span
        aria-label="Clear overflow"
        className="ms-1 inline-flex cursor-pointer items-center"
        onClick={(event) => {
          event.stopPropagation();
          onClearExtra();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            onClearExtra();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <X aria-hidden="true" className="h-3 w-3 shrink-0 cursor-pointer hover:text-foreground" />
      </span>
    </Badge>
  );
}

interface CommandFooterProps {
  clearText: string;
  hasSelected: boolean;
  hideClear: boolean;
  hideClose: boolean;
  onClear: () => void;
  onClose: () => void;
  resetText: string;
}

function CommandFooter({
  clearText,
  hasSelected,
  hideClear,
  hideClose,
  onClear,
  onClose,
  resetText,
}: CommandFooterProps): React.JSX.Element | null {
  const showClear = !hideClear && hasSelected;
  const showClose = !hideClose;

  if (!showClear && !showClose) return null;

  return (
    <CommandGroup>
      <div className="flex items-center justify-center">
        {showClear && (
          <CommandItem className="flex-1 cursor-pointer justify-center" onSelect={onClear}>
            {clearText}
          </CommandItem>
        )}
        {showClear && showClose && <Separator className="shrink-0 bg-border h-full min-h-6" orientation="vertical" />}
        {showClose && (
          <CommandItem className="flex-1 cursor-pointer justify-center" onSelect={onClose}>
            {resetText}
          </CommandItem>
        )}
      </div>
    </CommandGroup>
  );
}

interface CheckboxItemProps {
  isLocked?: boolean;
  isSelected: boolean;
  label: string;
  onToggle: () => void;
  optionIcon?: React.ComponentType<{ className?: string }>;
  /** Unique id — required so cmdk doesn't collide on two options sharing a label. */
  value: string;
}

function CheckboxItem({
  isLocked = false,
  isSelected,
  label,
  onToggle,
  optionIcon: OptionIcon,
  value,
}: CheckboxItemProps): React.JSX.Element {
  return (
    <CommandItem
      className={cn('py-1.5', isLocked ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer')}
      disabled={isLocked}
      keywords={[label]}
      onSelect={isLocked ? undefined : onToggle}
      value={value}
    >
      <div
        className={cn(
          'me-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary',
          isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
        )}
      >
        <CheckIcon className="h-4 w-4" />
      </div>
      {OptionIcon && <OptionIcon className="me-2 h-4 w-4 text-muted-foreground" />}
      <span>{label}</span>
    </CommandItem>
  );
}

interface MultiSelectTriggerProps {
  animation: number;
  clearExtraOptions: () => void;
  handleClear: () => void;
  isAnimating: boolean;
  label?: string;
  lockedValues: string[];
  maxCount: number;
  options: MultiSelectOption[];
  overflowClearable: boolean;
  placeholder: string;
  selectedValues: string[];
  showLabelWhenFiltering: boolean;
  toggleOption: (v: string) => void;
  variant: MultiSelectProps['variant'];
}

function MultiSelectTrigger({
  animation,
  clearExtraOptions,
  handleClear,
  isAnimating,
  label,
  lockedValues,
  maxCount,
  options,
  overflowClearable,
  placeholder,
  selectedValues,
  showLabelWhenFiltering,
  toggleOption,
  variant,
}: MultiSelectTriggerProps): React.JSX.Element {
  if (selectedValues.length === 0) {
    return (
      <div className="mx-auto flex w-full items-center justify-between">
        <div className="flex items-center">
          {label ? (
            <span className="me-2 text-sm text-muted-foreground">{label}</span>
          ) : (
            <span className="sr-only">{placeholder}</span>
          )}
          <span className="text-sm">{placeholder}</span>
        </div>
        <ChevronDown className="ms-2 h-4 w-4 cursor-pointer text-muted-foreground" />
      </div>
    );
  }

  const visibleSelected = selectedValues.slice(0, maxCount);
  const overflowCount = selectedValues.length - maxCount;

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex flex-wrap items-center">
        {label && showLabelWhenFiltering ? (
          <span className="me-2 text-sm text-muted-foreground">{label}</span>
        ) : (
          <span className="sr-only">{label ?? placeholder}</span>
        )}
        {visibleSelected.map((val) => {
          const option = options.find((o) => o.value === val);
          if (!option) return null;
          return (
            <SelectedBadge
              animation={animation}
              isAnimating={isAnimating}
              isLocked={lockedValues.includes(val)}
              key={val}
              onRemove={() => toggleOption(val)}
              option={option}
              variant={variant}
            />
          );
        })}
        {overflowCount > 0 && (
          <OverflowBadge
            animation={animation}
            clearable={overflowClearable}
            count={overflowCount}
            isAnimating={isAnimating}
            onClearExtra={clearExtraOptions}
            variant={variant}
          />
        )}
      </div>
      <div className="flex items-center justify-between">
        <span
          aria-label="Clear all"
          className="mx-2 inline-flex cursor-pointer items-center text-muted-foreground"
          onClick={(event) => {
            event.stopPropagation();
            handleClear();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              handleClear();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <XIcon aria-hidden="true" className="h-4 w-4" />
        </span>
        <Separator className="flex h-full min-h-6" orientation="vertical" />
        <ChevronDown className="ms-2 h-4 w-4 cursor-pointer text-muted-foreground" />
      </div>
    </div>
  );
}

interface SectionedOptionGroupsProps {
  lockedValues: string[];
  sections: MultiSelectSection[];
  selectedValues: string[];
  toggleOption: (value: string) => void;
}

function SectionedOptionGroups({
  lockedValues,
  sections,
  selectedValues,
  toggleOption,
}: SectionedOptionGroupsProps): React.JSX.Element {
  // Skip empty sections to avoid rendering a dangling heading.
  const nonEmptySections = sections.filter((section) => section.options.length > 0);

  return (
    <>
      {nonEmptySections.map((section, index) => (
        <CommandGroup heading={section.heading} key={section.heading ?? index}>
          {section.options.map((option) => (
            <CheckboxItem
              isLocked={lockedValues.includes(option.value)}
              isSelected={selectedValues.includes(option.value)}
              key={option.value}
              label={option.label}
              onToggle={() => toggleOption(option.value)}
              optionIcon={option.icon}
              value={option.value}
            />
          ))}
        </CommandGroup>
      ))}
    </>
  );
}

interface FlatOptionGroupProps {
  hideSelectAll: boolean;
  lockedValues: string[];
  options: MultiSelectOption[];
  selectAllText: string;
  selectedValues: string[];
  toggleAll: () => void;
  toggleOption: (value: string) => void;
  unselectAllText: string;
}

function FlatOptionGroup({
  hideSelectAll,
  lockedValues,
  options,
  selectAllText,
  selectedValues,
  toggleAll,
  toggleOption,
  unselectAllText,
}: FlatOptionGroupProps): React.JSX.Element {
  const allSelected = selectedValues.length === options.length;
  return (
    <CommandGroup>
      {!hideSelectAll && (
        <>
          <CheckboxItem
            isSelected={allSelected}
            label={allSelected ? unselectAllText : selectAllText}
            onToggle={toggleAll}
            value="__select_all__"
          />
          <CommandSeparator />
        </>
      )}
      {options.map((option) => (
        <CheckboxItem
          isLocked={lockedValues.includes(option.value)}
          isSelected={selectedValues.includes(option.value)}
          key={option.value}
          label={option.label}
          onToggle={() => toggleOption(option.value)}
          optionIcon={option.icon}
          value={option.value}
        />
      ))}
    </CommandGroup>
  );
}

interface DropdownSearchHeaderProps {
  async: boolean;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  loadingText: string;
  onSearchValueChange?: (value: string) => void;
  searchPlaceholder: string;
  searchPrompt?: string;
  searchable: boolean;
}

function DropdownSearchHeader({
  async,
  handleInputKeyDown,
  isLoading,
  loadingText,
  onSearchValueChange,
  searchPlaceholder,
  searchPrompt,
  searchable,
}: DropdownSearchHeaderProps): React.JSX.Element {
  const [searchText, setSearchText] = React.useState('');

  function handleSearchTextChange(next: string): void {
    setSearchText(next);
    onSearchValueChange?.(next);
  }

  const showSearchPrompt = async && !!searchPrompt && searchText === '';

  return (
    <>
      {searchable && (
        <CommandInput
          onKeyDown={handleInputKeyDown}
          onValueChange={async ? handleSearchTextChange : undefined}
          placeholder={searchPlaceholder}
        />
      )}
      {searchable && <CommandSeparator />}
      {showSearchPrompt && <div className="px-3 py-2 text-xs text-muted-foreground">{searchPrompt}</div>}
      {isLoading && <div className="px-3 py-2 text-xs text-muted-foreground">{loadingText}</div>}
    </>
  );
}

interface DropdownFooterSectionProps {
  clearText: string;
  hideClear: boolean;
  hideClose: boolean;
  hideFooter: boolean;
  onClear: () => void;
  onClose: () => void;
  resetText: string;
  selectedCount: number;
}

function DropdownFooterSection({
  clearText,
  hideClear,
  hideClose,
  hideFooter,
  onClear,
  onClose,
  resetText,
  selectedCount,
}: DropdownFooterSectionProps): React.JSX.Element | null {
  const showFooter = !hideFooter && (!hideClose || (!hideClear && selectedCount > 0));
  if (!showFooter) return null;

  return (
    <>
      <CommandSeparator />
      <CommandFooter
        clearText={clearText}
        hasSelected={selectedCount > 0}
        hideClear={hideClear}
        hideClose={hideClose}
        onClear={onClear}
        onClose={onClose}
        resetText={resetText}
      />
    </>
  );
}

interface MultiSelectDropdownProps {
  async: boolean;
  clearText: string;
  emptyText: string;
  handleClear: () => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  hideClear: boolean;
  hideClose: boolean;
  hideFooter: boolean;
  hideSelectAll: boolean;
  isLoading: boolean;
  loadingText: string;
  lockedValues: string[];
  onClose: () => void;
  onSearchValueChange?: (value: string) => void;
  options: MultiSelectOption[];
  resetText: string;
  resultsHint?: string;
  searchPlaceholder: string;
  searchPrompt?: string;
  searchable: boolean;
  sections?: MultiSelectSection[];
  selectAllText: string;
  selectedValues: string[];
  toggleAll: () => void;
  toggleOption: (option: string) => void;
  unselectAllText: string;
}

function MultiSelectDropdown({
  async,
  clearText,
  emptyText,
  handleClear,
  handleInputKeyDown,
  hideClear,
  hideClose,
  hideFooter,
  hideSelectAll,
  isLoading,
  loadingText,
  lockedValues,
  onClose,
  onSearchValueChange,
  options,
  resetText,
  resultsHint,
  searchPlaceholder,
  searchPrompt,
  searchable,
  sections,
  selectAllText,
  selectedValues,
  toggleAll,
  toggleOption,
  unselectAllText,
}: MultiSelectDropdownProps): React.JSX.Element {
  return (
    <Command
      className="[&_[cmdk-input-wrapper]]:mx-2 [&_[cmdk-input-wrapper]]:my-2 [&_[cmdk-input-wrapper]]:rounded-md [&_[cmdk-input-wrapper]]:border [&_[cmdk-input-wrapper]]:border-input [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:px-2 [&_[cmdk-input-wrapper]:focus-within]:ring-2 [&_[cmdk-input-wrapper]:focus-within]:ring-ring [&_[cmdk-input-wrapper]:focus-within]:ring-offset-2 [&_[cmdk-input]]:h-8 [&_[cmdk-input]]:py-1.5"
      shouldFilter={!async}
    >
      <DropdownSearchHeader
        async={async}
        handleInputKeyDown={handleInputKeyDown}
        isLoading={isLoading}
        loadingText={loadingText}
        onSearchValueChange={onSearchValueChange}
        searchPlaceholder={searchPlaceholder}
        searchPrompt={searchPrompt}
        searchable={searchable}
      />
      <CommandList>
        {/* Suppress the empty state while a request is in flight — otherwise "no results" flashes
            next to the loading row for the (common) case of zero items loaded so far. */}
        {!isLoading && <CommandEmpty>{emptyText}</CommandEmpty>}
        {sections?.length ? (
          <SectionedOptionGroups
            lockedValues={lockedValues}
            sections={sections}
            selectedValues={selectedValues}
            toggleOption={toggleOption}
          />
        ) : (
          <FlatOptionGroup
            hideSelectAll={hideSelectAll}
            lockedValues={lockedValues}
            options={options}
            selectAllText={selectAllText}
            selectedValues={selectedValues}
            toggleAll={toggleAll}
            toggleOption={toggleOption}
            unselectAllText={unselectAllText}
          />
        )}
        {resultsHint && <div className="px-3 py-2 text-xs text-muted-foreground">{resultsHint}</div>}
        <DropdownFooterSection
          clearText={clearText}
          hideClear={hideClear}
          hideClose={hideClose}
          hideFooter={hideFooter}
          onClear={handleClear}
          onClose={onClose}
          resetText={resetText}
          selectedCount={selectedValues.length}
        />
      </CommandList>
    </Command>
  );
}

// ─── Defaults ────────────────────────────────────────────────────────────────

interface ResolvedMultiSelectProps extends MultiSelectProps {
  animation: number;
  async: boolean;
  clearText: string;
  emptyText: string;
  hideClear: boolean;
  hideClose: boolean;
  hideFooter: boolean;
  hideSelectAll: boolean;
  isLoading: boolean;
  loadingText: string;
  lockedValues: string[];
  maxCount: number;
  modalPopover: boolean;
  options: MultiSelectOption[];
  overflowClearable: boolean;
  placeholder: string;
  resetText: string;
  searchPlaceholder: string;
  searchable: boolean;
  selectAllText: string;
  showLabelWhenFiltering: boolean;
  unselectAllText: string;
}

function resolveProps(props: MultiSelectProps): ResolvedMultiSelectProps {
  return {
    animation: 0,
    clearText: 'Clear',
    emptyText: 'No results found.',
    hideClear: false,
    hideClose: false,
    hideFooter: true,
    hideSelectAll: false,
    loadingText: 'Loading...',
    maxCount: DEFAULT_MULTI_SELECT_MAX_COUNT,
    modalPopover: false,
    overflowClearable: false,
    placeholder: 'Select options',
    resetText: 'Close',
    searchPlaceholder: 'Search...',
    searchable: true,
    selectAllText: 'Select All',
    showLabelWhenFiltering: false,
    unselectAllText: 'Unselect All',
    ...props,
    // Guards against callers explicitly passing `undefined`, which would otherwise win the spread.
    async: props.async ?? false,
    isLoading: props.isLoading ?? false,
    lockedValues: props.lockedValues ?? [],
    options: props.options ?? [],
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MultiSelect(rawProps: MultiSelectProps): React.JSX.Element {
  const {
    animation,
    async,
    className,
    clearText,
    defaultValue,
    emptyText,
    extraSelectedOptions,
    hideClear,
    hideClose,
    hideFooter,
    hideSelectAll,
    isLoading,
    label,
    loadingText,
    lockedValues,
    maxCount,
    modalPopover,
    onClick,
    onOpenChange,
    onSearchValueChange,
    onValueChange,
    options,
    overflowClearable,
    placeholder,
    resetText,
    resultsHint,
    searchable,
    searchPlaceholder,
    searchPrompt,
    sections,
    selectAllText,
    showLabelWhenFiltering,
    unselectAllText,
    value,
    variant,
    ...rest
  } = resolveProps(rawProps);

  // In sections mode, flattened section options drive selection instead of `options`.
  const effectiveOptions = sections?.length ? sections.flatMap((section) => section.options) : options;
  const resolvedHideSelectAll = hideSelectAll || async;

  const {
    clearExtraOptions,
    handleClear,
    handleInputKeyDown,
    isAnimating,
    isPopoverOpen,
    selectedValues,
    setIsAnimating,
    setIsPopoverOpen,
    toggleAll,
    toggleOption,
  } = useMultiSelect(value, defaultValue, onValueChange, effectiveOptions, lockedValues);

  // Single choke point for every open/close path; also resets the caller's async search text on
  // close, since the search input unmounts and would otherwise leave a stale query for next open.
  const handleOpenChange = React.useCallback(
    (open: boolean): void => {
      setIsPopoverOpen(open);
      onOpenChange?.(open);
      if (async && !open) {
        onSearchValueChange?.('');
      }
    },
    [setIsPopoverOpen, onOpenChange, async, onSearchValueChange],
  );

  const closePopover = React.useCallback((): void => handleOpenChange(false), [handleOpenChange]);
  const toggleAnimation = React.useCallback((): void => setIsAnimating((a) => !a), [setIsAnimating]);
  const handleClearExtra = React.useCallback((): void => clearExtraOptions(maxCount), [clearExtraOptions, maxCount]);
  const showWand = animation > 0 && selectedValues.length > 0;

  // The trigger resolves selected-badge labels from the dropdown options plus any extra selected
  // options (preserved out-of-filter selections); the dropdown list itself stays on `options`.
  const triggerOptions = extraSelectedOptions?.length
    ? [...effectiveOptions, ...extraSelectedOptions]
    : effectiveOptions;

  return (
    <Popover modal={modalPopover} onOpenChange={handleOpenChange} open={isPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          {...rest}
          className={cn(
            'flex h-auto min-h-9 w-full items-center justify-between rounded-md border bg-inherit [&_svg]:pointer-events-auto',
            className,
          )}
          onClick={(event) => {
            onClick?.(event);
            if (!event.defaultPrevented) {
              handleOpenChange(!isPopoverOpen);
            }
          }}
          variant="outline"
        >
          <MultiSelectTrigger
            animation={animation}
            clearExtraOptions={handleClearExtra}
            handleClear={handleClear}
            isAnimating={isAnimating}
            label={label}
            lockedValues={lockedValues}
            maxCount={maxCount}
            options={triggerOptions}
            overflowClearable={overflowClearable}
            placeholder={placeholder}
            selectedValues={selectedValues}
            showLabelWhenFiltering={showLabelWhenFiltering}
            toggleOption={toggleOption}
            variant={variant}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0" onEscapeKeyDown={closePopover}>
        <MultiSelectDropdown
          async={async}
          clearText={clearText}
          emptyText={emptyText}
          handleClear={handleClear}
          handleInputKeyDown={handleInputKeyDown}
          hideClear={hideClear}
          hideClose={hideClose}
          hideFooter={hideFooter}
          hideSelectAll={resolvedHideSelectAll}
          isLoading={isLoading}
          loadingText={loadingText}
          lockedValues={lockedValues}
          onClose={closePopover}
          onSearchValueChange={onSearchValueChange}
          options={options}
          resetText={resetText}
          resultsHint={resultsHint}
          searchable={searchable}
          searchPlaceholder={searchPlaceholder}
          searchPrompt={searchPrompt}
          sections={sections}
          selectAllText={selectAllText}
          selectedValues={selectedValues}
          toggleAll={toggleAll}
          toggleOption={toggleOption}
          unselectAllText={unselectAllText}
        />
      </PopoverContent>
      {showWand && (
        <WandSparkles
          className={cn('my-2 h-3 w-3 cursor-pointer bg-background text-foreground', isAnimating ? '' : 'text-muted')}
          onClick={toggleAnimation}
        />
      )}
    </Popover>
  );
}
